import type { SignalKey } from '@api/devices/contracts';
import { signalKeys } from '@api/devices/contracts';
import type { OsValue, RecordKind, SearchQueryInput, StatusValue } from '@contracts';
import { osValues, recordKinds, sortFields, statusValues } from '@contracts';
import { absolute, DAY, HOUR, minuteFloor } from '../lib/time';

export type SortField = typeof sortFields[number];
export interface Sort { field: SortField; direction: 'asc' | 'desc' }
export type Unit = 'h' | 'd';
export type SeenFilter =
  | { type: 'within'; amount: number; unit: Unit }
  | { type: 'older'; amount: number; unit: Unit; includeNever: boolean }
  | { type: 'before'; at: string; includeNever: boolean }
  | { type: 'after'; at: string }
  | { type: 'never' };

/** Everything a list view, link or interpreted question can express. */
export interface FilterState {
  q: string;
  kind: RecordKind | 'all';
  org: string[]; site: string[];
  os: OsValue[]; status: StatusValue[];
  seen: SeenFilter | null;
  /** Devices with any of these open health signals. */
  signal: SignalKey[];
  /** Devices with installed software whose name contains this text. */
  software: string;
  duplicateName: boolean;
  sort: Sort | null; // null: relevance with text, otherwise name
  page: number; size: number;
}

export const PAGE_SIZES = [25, 50, 100] as const;
/** The API reads at most 10,000 records in; the last reachable page depends on the page size. */
export const MAX_OFFSET = 10_000;
export const lastPage = (size: number) => Math.floor(MAX_OFFSET / size) + 1;
export const emptyFilters: FilterState = {
  q: '', kind: 'all', org: [], site: [], os: [], status: [], seen: null, signal: [], software: '', duplicateName: false, sort: null, page: 1, size: 25,
};

export const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const list = (value: string | null) => (value ?? '').split(',').map(v => v.trim()).filter(Boolean);
const unique = <T,>(values: T[]) => [...new Set(values)];

/** The longest check-in spans a link can carry; the question reader clamps to the same limits. */
export const SEEN_LIMITS: Record<Unit, number> = { d: 3650, h: 999 };

function parseSeen(value: string): SeenFilter | null {
  if (value === 'never') return { type: 'never' };
  const [head = '', ...rest] = value.split(',');
  const includeNever = rest.length === 1 && rest[0] === 'never';
  if (rest.length > (includeNever ? 1 : 0)) return null;
  const colon = head.indexOf(':');
  const type = head.slice(0, colon), arg = head.slice(colon + 1);
  const relative = /^(\d{1,4})([hd])$/.exec(arg);
  if ((type === 'within' || type === 'older') && relative) {
    const amount = Number(relative[1]), unit = relative[2] as Unit;
    if (amount < 1 || amount > SEEN_LIMITS[unit]) return null;
    if (type === 'within') return includeNever ? null : { type, amount, unit };
    return { type, amount, unit, includeNever };
  }
  if ((type === 'before' || type === 'after') && !Number.isNaN(Date.parse(arg)) && /T.*(Z|[+-]\d\d:\d\d)$/.test(arg)) {
    if (type === 'after') return includeNever ? null : { type, at: new Date(arg).toISOString() };
    return { type, at: new Date(arg).toISOString(), includeNever };
  }
  return null;
}

export function encodeSeen(seen: SeenFilter): string {
  const never = 'includeNever' in seen && seen.includeNever ? ',never' : '';
  switch (seen.type) {
    case 'never': return 'never';
    case 'within': return `within:${seen.amount}${seen.unit}`;
    case 'older': return `older:${seen.amount}${seen.unit}${never}`;
    case 'before': return `before:${seen.at}${never}`;
    case 'after': return `after:${seen.at}`;
  }
}

/** Reads link state. Anything unrecognised is dropped and reported, never guessed. */
export function parseFilters(params: URLSearchParams, base: Partial<FilterState> = {}): { state: FilterState; invalid: string[] } {
  const state: FilterState = { ...emptyFilters, ...base };
  const invalid: string[] = [];
  const q = params.get('q');
  if (q !== null) { if (q.trim().length <= 200) state.q = q; else invalid.push('q'); }
  const kind = params.get('kind');
  if (kind !== null) {
    // "all" is the default, but an explicit kind=all (as Ask writes to mark a link as filters) is valid too.
    if (kind === 'all' || (recordKinds as readonly string[]).includes(kind)) state.kind = kind as RecordKind | 'all'; else invalid.push('kind');
  }
  // A constraint that is present but empty is invalid too: dropping it would silently widen the scope.
  for (const key of ['org', 'site'] as const) {
    if (!params.has(key)) continue;
    const values = list(params.get(key));
    if (values.length && values.length <= 20 && values.every(v => uuid.test(v))) state[key] = unique(values.map(v => v.toLowerCase()));
    else invalid.push(key);
  }
  const os = list(params.get('os'));
  if (params.has('os')) { if (os.length && os.every(v => (osValues as readonly string[]).includes(v))) state.os = unique(os) as OsValue[]; else invalid.push('os'); }
  const status = list(params.get('status'));
  if (params.has('status')) {
    if (status.length && status.every(v => (statusValues as readonly string[]).includes(v))) state.status = unique(status) as StatusValue[];
    else invalid.push('status');
  }
  const seen = params.get('seen');
  if (seen !== null) { const parsed = parseSeen(seen); if (parsed) state.seen = parsed; else invalid.push('seen'); }
  const signal = list(params.get('signal'));
  if (params.has('signal')) {
    if (signal.length && signal.every(v => (signalKeys as readonly string[]).includes(v))) state.signal = unique(signal) as SignalKey[]; else invalid.push('signal');
  }
  const software = params.get('software');
  if (software !== null) { const t = software.trim(); if (t.length >= 2 && t.length <= 100) state.software = t; else invalid.push('software'); }
  if (params.has('duplicateName')) {
    if (params.get('duplicateName') === '1') state.duplicateName = true; else invalid.push('duplicateName');
  }
  const sort = params.get('sort');
  if (sort !== null) {
    const [field, direction] = sort.split('.');
    if ((sortFields as readonly string[]).includes(field ?? '') && (direction === 'asc' || direction === 'desc')) {
      state.sort = { field: field as SortField, direction };
    } else invalid.push('sort');
  }
  const page = params.get('page');
  if (page !== null) { const n = Number(page); if (Number.isInteger(n) && n >= 1 && n <= 401) state.page = n; else invalid.push('page'); }
  const size = params.get('size');
  if (size !== null) { const n = Number(size); if ((PAGE_SIZES as readonly number[]).includes(n)) state.size = n; else invalid.push('size'); }
  if (state.page > lastPage(state.size)) { state.page = 1; invalid.push('page'); }
  if (state.kind === 'organization' && (state.site.length || hasDeviceFilters(state))) { state.kind = 'all'; invalid.push('kind'); }
  return { state, invalid };
}

/** Writes only what differs from the page's defaults, so links stay short. */
export function writeFilters(state: FilterState, base: Partial<FilterState> = {}): URLSearchParams {
  const defaults = { ...emptyFilters, ...base };
  const params = new URLSearchParams();
  if (state.q.trim()) params.set('q', state.q.trim());
  if (state.kind !== defaults.kind) params.set('kind', state.kind);
  for (const key of ['org', 'site', 'os', 'status', 'signal'] as const) {
    if (state[key].length && state[key].join(',') !== defaults[key].join(',')) params.set(key, state[key].join(','));
  }
  if (state.seen) params.set('seen', encodeSeen(state.seen));
  if (state.software.trim()) params.set('software', state.software.trim());
  if (state.duplicateName) params.set('duplicateName', '1');
  if (state.sort) params.set('sort', `${state.sort.field}.${state.sort.direction}`);
  if (state.page > 1) params.set('page', String(state.page));
  if (state.size !== defaults.size) params.set('size', String(state.size));
  return params;
}

export const hasDeviceFilters = (state: Pick<FilterState, 'os' | 'status' | 'seen' | 'signal' | 'software' | 'duplicateName'>) =>
  state.os.length > 0 || state.status.length > 0 || state.seen !== null || state.signal.length > 0 || state.software.trim().length > 0 || state.duplicateName;

/** Device-only filters or sorts restrict the search to devices. */
export function effectiveKinds(state: FilterState): RecordKind[] {
  if (hasDeviceFilters(state) || state.sort?.field === 'status' || state.sort?.field === 'lastSeenAt') return ['device'];
  if (state.kind !== 'all') return [state.kind];
  return state.site.length ? ['site', 'device'] : [...recordKinds];
}

const unitMs = (unit: Unit) => (unit === 'h' ? HOUR : DAY);

/** Resolves relative check-in filters against a fixed instant. */
export function seenBoundary(seen: SeenFilter, now = minuteFloor()): string | null {
  switch (seen.type) {
    case 'within': case 'older': return new Date(now - seen.amount * unitMs(seen.unit)).toISOString();
    case 'before': case 'after': return seen.at;
    case 'never': return null;
  }
}

/** A fully specified query: every field the API would otherwise default is explicit. */
export type BuiltQuery = SearchQueryInput & { q: string; kinds: RecordKind[]; sort: Sort; limit: number; offset: number };

export function toSearchQuery(state: FilterState, now = minuteFloor()): BuiltQuery {
  const groups: NonNullable<SearchQueryInput['filters']> = [];
  if (state.org.length) groups.push({ mode: 'and', clauses: [{ field: 'organizationId', op: 'in', values: state.org }] });
  if (state.site.length) groups.push({ mode: 'and', clauses: [{ field: 'siteId', op: 'in', values: state.site }] });
  if (state.os.length) groups.push({ mode: 'and', clauses: [{ field: 'os', op: 'in', values: state.os }] });
  if (state.status.length) groups.push({ mode: 'and', clauses: [{ field: 'status', op: 'in', values: state.status }] });
  const seen = state.seen;
  if (seen) {
    const at = seenBoundary(seen, now);
    if (seen.type === 'never') groups.push({ mode: 'and', clauses: [{ field: 'lastSeenAt', op: 'unknown' }] });
    else if (seen.type === 'within' || seen.type === 'after') {
      groups.push({ mode: 'and', clauses: [{ field: 'lastSeenAt', op: 'after', value: at! }] });
    } else {
      const before = { field: 'lastSeenAt' as const, op: 'before' as const, value: at! };
      groups.push(seen.includeNever
        ? { mode: 'or', clauses: [before, { field: 'lastSeenAt', op: 'unknown' }] }
        : { mode: 'and', clauses: [before] });
    }
  }
  if (state.signal.length) groups.push({ mode: 'and', clauses: [{ field: 'signal', op: 'in', values: state.signal }] });
  if (state.software.trim().length >= 2) groups.push({ mode: 'and', clauses: [{ field: 'software', op: 'contains', value: state.software.trim() }] });
  if (state.duplicateName) groups.push({ mode: 'and', clauses: [{ field: 'duplicateName', op: 'is', value: true }] });
  const sort: Sort = state.sort ?? (state.q.trim() ? { field: 'relevance', direction: 'desc' } : { field: 'title', direction: 'asc' });
  return {
    version: 1, q: state.q.trim(), kinds: effectiveKinds(state), filters: groups, sort,
    limit: state.size, offset: Math.min(MAX_OFFSET, (state.page - 1) * state.size),
  };
}

export const activeFilterCount = (state: FilterState) =>
  state.org.length + state.site.length + state.os.length + state.status.length + (state.seen ? 1 : 0) + state.signal.length + (state.software ? 1 : 0) + (state.duplicateName ? 1 : 0);

export const signalLabels: Record<SignalKey, string> = {
  'disk.low': 'Low disk space', 'disk.filling': 'Disk filling up', 'memory.pressure': 'Memory pressure', 'cpu.sustained': 'CPU saturated',
  'reboot.pending': 'Restart pending', 'uptime.long': 'Long uptime', 'security.av_off': 'Antivirus off', 'security.av_stale': 'Definitions out of date',
  'security.firewall_off': 'Firewall off', 'security.encryption_off': 'Disk not encrypted', 'security.remote_inbound': 'Remote reachable from network',
  'os.unsupported': 'Unsupported OS',
  'os.ending': 'OS support ending', 'patch.stale': 'Updates overdue', 'patch.paused': 'Updates paused',
};
export const signalGroups: Array<[string, SignalKey[]]> = [
  ['Protection', ['security.av_off', 'security.av_stale', 'security.firewall_off', 'security.encryption_off', 'security.remote_inbound']],
  ['Updates and support', ['os.unsupported', 'os.ending', 'patch.stale', 'patch.paused', 'reboot.pending']],
  ['Storage', ['disk.low', 'disk.filling']], ['Performance', ['memory.pressure', 'cpu.sustained', 'uptime.long']],
];

const olderLabel = (amount: number, unit: Unit) =>
  unit === 'h' ? `${amount}+ hour${amount === 1 ? '' : 's'}` : amount === 1 ? '24+ hours' : `${amount}+ days`;
const amountLabel = (amount: number, unit: Unit) =>
  unit === 'h' ? `${amount} hour${amount === 1 ? '' : 's'}` : amount === 1 ? '24 hours' : `${amount} days`;

/** Human label, a short chip form, and the exact boundary the server will apply. */
export function describeSeen(seen: SeenFilter, now = minuteFloor()): { label: string; short: string; exact: string } {
  const at = seenBoundary(seen, now);
  switch (seen.type) {
    case 'never': return { label: 'Never checked in', short: 'never', exact: 'No check-in has ever been recorded' };
    case 'within': return { label: `Checked in within ${amountLabel(seen.amount, seen.unit)}`, short: `within ${amountLabel(seen.amount, seen.unit)}`, exact: `Last check-in after ${absolute(at!)}` };
    case 'older': return {
      label: `No check-in for ${amountLabel(seen.amount, seen.unit)}${seen.includeNever ? ', or never' : ''}`,
      short: `${olderLabel(seen.amount, seen.unit)} ago${seen.includeNever ? ', or never' : ''}`,
      exact: `Last check-in before ${absolute(at!)}${seen.includeNever ? ', or none recorded' : ''}`,
    };
    case 'before': return {
      label: `Last check-in before ${absolute(seen.at)}${seen.includeNever ? ', or never' : ''}`,
      short: `before ${absolute(seen.at)}${seen.includeNever ? ', or never' : ''}`,
      exact: `Before ${absolute(seen.at)}${seen.includeNever ? ', or none recorded' : ''}`,
    };
    case 'after': return { label: `Checked in after ${absolute(seen.at)}`, short: `after ${absolute(seen.at)}`, exact: `After ${absolute(seen.at)}` };
  }
}
